import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  Users,
  Lock,
  Eye,
  FileText,
  Phone,
  CreditCard,
  Star,
  Camera,
} from 'lucide-react';
import { SafetySkeleton } from '../../components/shared/ClientPageSkeleton';
import { siteSettingsService } from '../../services/siteSettingsService';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingImagePair,
  MarketingPrimaryButton,
  MarketingSecondaryButton,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
} from '../../components/home/MarketingPageChrome';

const SAFETY_HERO =
  'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1800&q=80';

const Safety = () => {
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const ss = settingsResponse?.data?.safety_settings;
  const content = useMemo(
    () => ({
      hero: {
        title: ss?.hero?.title || 'Creating a safe, trustworthy marketplace',
        subtitle: ss?.hero?.subtitle || '',
      },
      guidelines: Array.isArray(ss?.guidelines) ? ss.guidelines : [],
      verification: Array.isArray(ss?.verification) ? ss.verification : [],
      reporting: Array.isArray(ss?.reporting) ? ss.reporting : [],
      resources: Array.isArray(ss?.resources) ? ss.resources : [],
      emergency: {
        title: ss?.emergency?.title ?? '',
        description: ss?.emergency?.description ?? '',
        phone: ss?.emergency?.phone ?? '',
        reportButtonText: ss?.emergency?.reportButtonText ?? '',
      },
    }),
    [ss]
  );

  const featureItems = useMemo(() => {
    const fromCms = content.guidelines.slice(0, 8).map((g) => ({
      icon: Shield,
      title: g.title,
      description: g.description,
    }));
    if (fromCms.length >= 4) return fromCms;
    return [
      { icon: Eye, title: 'Risk monitoring', description: 'We watch for unusual booking activity.' },
      { icon: Users, title: 'Identity verification', description: 'Vendors and clients are reviewed before listing.' },
      { icon: FileText, title: 'Clear policies', description: 'Community guidelines everyone agrees to follow.' },
      { icon: Shield, title: 'Liability awareness', description: 'Guidance so events stay covered and calm.' },
      { icon: CreditCard, title: 'Protected payments', description: 'Checkout stays on-platform and secure.' },
      { icon: Star, title: 'Real reviews', description: 'Feedback from completed bookings only.' },
      { icon: Lock, title: 'Fraud protection', description: 'Keep messaging and payments inside VendaList.' },
      { icon: Camera, title: 'Additional safety', description: 'Tips for day-of hosting and attending.' },
    ];
  }, [content.guidelines]);

  const storyBlocks = useMemo(() => {
    const v = content.verification[0];
    const r = content.reporting[0];
    return [
      {
        image:
          'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80',
        title: v?.title || 'Protecting the community',
        description:
          v?.description ||
          'We’re committed to a respectful marketplace — clear expectations for vendors, clients, and day-of events.',
        linkLabel: 'Report a problem',
        to: '/report',
      },
      {
        image:
          'https://images.unsplash.com/photo-1517935703315-d146452819c0?auto=format&fit=crop&w=1200&q=80',
        title: r?.title || 'How to report issues',
        description:
          r?.description ||
          'If something feels off before, during, or after a booking, tell us. We review every report carefully.',
        linkLabel: 'Contact support',
        to: '/contact',
      },
    ];
  }, [content.verification, content.reporting]);

  if (isLoading) {
    return <SafetySkeleton />;
  }

  return (
    <MarketingPage>
      <MarketingMediaHero
        title={content.hero.title}
        subtitle={
          content.hero.subtitle ||
          'Safety for vendors and clients — so every booking can focus on the event.'
        }
        image={SAFETY_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#safety-features">How we protect you</MarketingHeroLightButton>
            <MarketingGhostOnDark to="/report">Report a problem</MarketingGhostOnDark>
          </>
        }
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="Trust & safety with you in mind">
          <p>
            VendaList brings people together for real events. We’re committed to the safety of
            clients and vendors — a trustworthy marketplace is the foundation of every booking.
          </p>
          <p>
            Here’s what we do behind the scenes to reduce risk and create peace of mind before,
            during, and after your event.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" id="safety-features" className="!pt-0 sm:!pt-0">
        <MarketingFeatureGrid items={featureItems} />
      </MarketingSection>

      <MarketingSection tone="canvas">
        <MarketingImagePair items={storyBlocks} />
      </MarketingSection>

      {(content.emergency.title || content.emergency.description) && (
        <MarketingSection tone="white">
          <MarketingReveal className="mx-auto flex max-w-3xl items-start gap-4">
            <AlertTriangle className="mt-1 h-5 w-5 shrink-0" style={{ color: '#0a0a0a' }} />
            <div>
              <h3 className="mb-2 text-xl font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
                {content.emergency.title}
              </h3>
              <p className="mb-6 text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
                {content.emergency.description}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {content.emergency.phone ? (
                  <a
                    href={`tel:${content.emergency.phone}`}
                    className="marketing-btn marketing-btn-ink gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    {content.emergency.phone}
                  </a>
                ) : null}
                {content.emergency.reportButtonText ? (
                  <MarketingSecondaryButton to="/report">
                    {content.emergency.reportButtonText}
                  </MarketingSecondaryButton>
                ) : null}
              </div>
            </div>
          </MarketingReveal>
        </MarketingSection>
      )}

      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            Need help? We’re here.
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            Support is available for questions and safety escalations.
          </p>
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <MarketingPrimaryButton
              to="/contact"
              className="!bg-white !text-m-ink hover:!bg-white/90"
            >
              Contact support
            </MarketingPrimaryButton>
            <MarketingGhostOnDark to="/help">Help center</MarketingGhostOnDark>
          </div>
        </MarketingReveal>
      </MarketingSection>
    </MarketingPage>
  );
};

export default Safety;
